import { useTranslation } from "react-i18next";
import { useForm, useWatch } from "react-hook-form";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  appViewSchema,
  assignableRoleSchema,
  intersectViewsWithRole,
  type AppView,
  type UpdateUserRequest,
  type User,
} from "@/contracts";
import { z } from "zod";

import { updateUserMutation } from "@/api/queries";
import { useAuth } from "@/auth/auth-context";
import { Alert } from "@/components/ui/alert";
import { SelectField, TextField } from "@/components/ui/field";
import { FormDialog } from "@/components/ui/form-dialog";
import { useApiClient } from "@/lib/api-context";
import { ApiError } from "@/lib/api-error";
import { ROLE_LABELS, USER_STATUS } from "./user-labels";
import { ViewAccessFields } from "./view-access-fields";

/** INVITED is absent: it is where an account starts, not somewhere to move it back to. */
const editableStatusSchema = z.enum(["ACTIVE", "DISABLED"]);

const editFormFields = z.object({
  name: z.string().trim().min(2, "Enter a name of at least 2 characters.").max(120),
  role: assignableRoleSchema,
  status: editableStatusSchema,
  views: z.array(appViewSchema),
});

type EditFormValues = z.infer<typeof editFormFields>;

function sameViews(a: readonly AppView[], b: readonly AppView[]): boolean {
  if (a.length !== b.length) {
    return false;
  }

  const left = new Set(a);

  return b.every((view) => left.has(view));
}

export function UserEditDialog({ user, onClose }: { user: User; onClose: () => void }) {
  const client = useApiClient();
  const { t } = useTranslation("pages");
  const { t: tCommon } = useTranslation("common");
  const { user: currentUser } = useAuth();
  const queryClient = useQueryClient();

  /**
   * Self-service through an admin endpoint is how an account locks itself
   * out, or quietly promotes itself, so the API refuses both. Hiding the
   * controls is kinder than letting the request come back 403.
   */
  const isSelf = user.id === currentUser?.id;
  // A platform admin's role comes from their user record rather than from a
  // membership (ADR-016), so a role picker here would change nothing.
  const showRole = !isSelf && user.role !== "SUPER_ADMIN";
  const showViews = !isSelf && user.role !== "SUPER_ADMIN";

  const initialRole = user.role === "ADMIN" ? "ADMIN" : "USER";

  const {
    register,
    handleSubmit,
    setError,
    control,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<EditFormValues>({
    resolver: zodResolver(editFormFields),
    defaultValues: {
      name: user.name,
      role: initialRole,
      status: user.status === "DISABLED" ? "DISABLED" : "ACTIVE",
      views: [...user.views],
    },
  });

  const role = useWatch({ control, name: "role" });
  const status = useWatch({ control, name: "status" });
  const views = useWatch({ control, name: "views" });

  const mutation = useMutation({
    ...updateUserMutation(client),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["users"] });
    },
  });

  const onSubmit = handleSubmit(async (values) => {
    // Only what actually changed: the contract rejects an empty patch, and
    // sending the current role back would be an audit entry about nothing.
    const body: UpdateUserRequest = {
      ...(values.name === user.name ? {} : { name: values.name }),
      ...(showRole && values.role !== user.role ? { role: values.role } : {}),
      ...(!isSelf && values.status !== user.status ? { status: values.status } : {}),
      ...(showViews && !sameViews(values.views, user.views) ? { views: values.views } : {}),
    };

    if (Object.keys(body).length === 0) {
      onClose();

      return;
    }

    try {
      await mutation.mutateAsync({ id: user.id, body });
      onClose();
    } catch (error) {
      if (error instanceof ApiError) {
        for (const [field, message] of Object.entries(error.fieldErrors())) {
          if (field in editFormFields.shape) {
            setError(field as keyof EditFormValues, { message });
          }
        }
      }
    }
  });

  return (
    <FormDialog
      open
      title={t("editNamedTitle", { name: user.name })}
      description={user.email}
      submitLabel={t("saveChanges")}
      pending={isSubmitting || mutation.isPending}
      onSubmit={(event) => void onSubmit(event)}
      onCancel={onClose}
    >
      <div className="flex flex-col gap-4">
        {mutation.isError ? <Alert>{mutation.error.message}</Alert> : null}

        <TextField
          label={t("fieldFullName")}
          required
          {...(errors.name?.message === undefined ? {} : { error: errors.name.message })}
          {...register("name")}
        />

        {showRole ? (
          <SelectField
            label={tCommon("role")}
            value={role}
            onChange={(value) => {
              const nextRole = value as EditFormValues["role"];
              setValue("role", nextRole, { shouldValidate: true, shouldDirty: true });
              setValue("views", intersectViewsWithRole(views ?? [], nextRole), {
                shouldDirty: true,
              });
            }}
            data={assignableRoleSchema.options.map((roleOption) => ({
              value: roleOption,
              label: ROLE_LABELS[roleOption],
            }))}
          />
        ) : null}

        {isSelf ? null : (
          <SelectField
            label={tCommon("status")}
            hint={
              user.status === "INVITED"
                ? t("statusDisableInvitedHint")
                : t("statusDisableActiveHint")
            }
            value={status}
            onChange={(value) =>
              setValue("status", value as EditFormValues["status"], {
                shouldValidate: true,
                shouldDirty: true,
              })
            }
            data={editableStatusSchema.options.map((statusOption) => ({
              value: statusOption,
              label: USER_STATUS[statusOption].label,
            }))}
          />
        )}

        {showViews ? (
          <ViewAccessFields
            role={role}
            value={views ?? []}
            onChange={(next) => setValue("views", next, { shouldDirty: true })}
          />
        ) : null}

        {isSelf ? (
          <Alert tone="info" title={t("dialogEditUserOwnTitle")} announce={false}>
            {t("dialogEditUserOwnBody")}
          </Alert>
        ) : null}
      </div>
    </FormDialog>
  );
}
